import React from "react";
import { useLang } from "@/lib/i18n";
import { ASSETS } from "@/lib/assets";
import { ShieldCheck, Check, Quote } from "lucide-react";

export default function About() {
  const { t } = useLang();
  const features = t("about.features");

  return (
    <section id="about" className="py-20 lg:py-28 border-y border-[var(--border-gold)]" style={{ background: "var(--at-darkest)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="relative animate-fade-up">
          <div className="corner-bracket relative rounded-3xl overflow-hidden ring-1 ring-[var(--border-gold)]">
            <img src={ASSETS.office} alt="Markus Huber analysing football data" data-testid="about-image" className="w-full h-[360px] sm:h-[480px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#38040C]/85 via-transparent to-transparent" />
          </div>
          <div data-testid="about-verified" className="absolute -bottom-5 left-5 at-card-solid rounded-2xl px-5 py-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-[#38040C]" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}><ShieldCheck size={22} /></div>
            <div>
              <div className="font-heading font-bold text-sm text-white leading-tight">{t("about.verified")}</div>
              <div className="text-xs text-white/60 font-body">Austria Tips · 2005–2026</div>
            </div>
          </div>
        </div>

        <div className="animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center gap-3 mb-4">
            <span className="font-heading text-xs font-extrabold tracking-[0.25em] uppercase gold-gradient-text">04</span>
            <span className="w-8 h-px bg-[var(--trust-gold)]/50" />
            <span className="font-heading text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{t("about.kicker")}</span>
          </div>
          <h2 className="font-serif-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("about.title")}</h2>
          <div className="text-sm font-heading font-semibold text-[var(--trust-gold)] mt-2 uppercase tracking-wide">{t("about.role")}</div>

          <p className="mt-6 text-base font-body text-white/80 leading-relaxed">{t("about.p1")}</p>
          <p className="mt-4 text-base font-body text-white/80 leading-relaxed">{t("about.p2")}</p>

          <div className="mt-7 relative border-l-2 border-[var(--trust-gold)] pl-5 py-1">
            <Quote size={18} className="text-[var(--trust-gold)]/50 mb-1" />
            <p className="font-serif-display font-semibold text-xl text-white italic leading-snug">{t("about.quote")}</p>
            <img src={ASSETS.signature} alt="signature" className="h-12 w-auto sig-blend mt-3" data-testid="about-signature" />
          </div>

          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {features.map((f, i) => (
              <li key={i} data-testid={`about-feature-${i}`} className="flex items-start gap-2.5">
                <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[#38040C]" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}><Check size={13} /></span>
                <span className="text-sm font-body text-white/90 font-medium">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
